import {
  FuelButton,
  FuelCallout,
  FuelCheckbox,
  FuelDropdown,
  FuelIcon,
  FuelTextField,
  FuelTypography,
} from '@frete.com/fuel-react';
import AppContext from 'context';
import { TAreaCode } from 'context/reducers/truckers.type';
import { useAmplitude } from 'hooks/useAmplitude';
import { useTracking } from 'hooks/useTracking';
import React, { FC, KeyboardEvent, MouseEvent, useContext, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { FuelFlex } from '@frete.com/fuel-station-react';
import { dsTheme } from 'constants/styled.contants';

const DDD: FC<{
  checked: number[];
  setChecked: (value: number[]) => void;
}> = ({ checked, setChecked }) => {
  const [search, setSearch] = useState('');

  const SEARCH_FIELD_MAX_LENGTH = 2;

  const { sendAmplitude } = useAmplitude();
  const { sendTracking } = useTracking();
  const { freightId } = useParams();

  const {
    state: {
      truckers: {
        meta: { areaCodes },
      },
    },
  } = useContext(AppContext);

  const areaCodeSorted = [...areaCodes].sort((a, b) => a.areaCode - b.areaCode);

  const [areaCodeDropDown, setAreaCodeDropDown] = useState(areaCodeSorted);
  const checkboxRef = React.useRef(null);
  const dropdownRef = useRef(null);

  const handleCheckedStatus = (id: number) => (isCheck: boolean) => {
    if (isCheck) {
      setChecked([...checked, id]);
      sendAmplitude('trucker_listing_filters_search_ddd', {
        type: 'busca-ddd',
        freightCode: freightId,
        value: id.toString(),
      });
      sendTracking({
        event: {
          name: `trucker_listing_filters_search_ddd`,
        },
        data: {
          type: 'busca-ddd',
          freightId: `hashed|${freightId}`,
          value: id.toString(),
        },
      });
    } else {
      setChecked(checked.filter((element) => element !== id));
    }
  };
  const re = /^[0-9\b]+$/;
  const isValidCharacter = (key: string): boolean =>
    !!(RegExp(re).exec(key) || key === 'Backspace' || key === 'Delete');

  const getTermFromEvent = (event: KeyboardEvent | ClipboardEvent) => {
    const target = event.target as HTMLInputElement;

    const { value: term } = target;
    return term;
  };

  const handleKeyUp = (event: KeyboardEvent): void => {
    if (!isValidCharacter(event.key)) {
      event.preventDefault();
      return;
    }
    if (event.type === 'keyup' && isValidCharacter(event.key)) {
      setSearch(getTermFromEvent(event));
    }
  };

  const handleKeyPress = (event: KeyboardEvent): void => {
    event.stopPropagation();
    const { key } = event;

    if (key === 'Escape') {
      dropdownRef?.current?.close();
    }
    if (!isValidCharacter(key)) {
      event.preventDefault();
    }
  };

  const checkBoxMap = ({ areaCode, text }: { areaCode: number; text: string }) => (
    <FuelCheckbox
      name="area-code"
      key={areaCode}
      data-testid={`area-code-${areaCode}`}
      label={`${areaCode} - ${text} e Região`}
      id={`area-code-${String(areaCode)}`}
      onInput={(event: React.ChangeEvent<HTMLFuelCheckboxElement>) => {
        handleCheckedStatus(areaCode)(event.target.checked);
      }}
      ref={checkboxRef}
      checked={checked.includes(areaCode)}
    />
  );

  const searchFilter = areaCodes.filter((areaCode: TAreaCode) => String(areaCode.areaCode) === search);

  const checkedFilterSearch = checked.includes(Number(search));

  useEffect(() => {
    if (search?.length === 0) {
      setAreaCodeDropDown(areaCodeSorted);
    } else if (search?.length === 1) {
      setAreaCodeDropDown(areaCodeSorted.filter(({ areaCode }) => String(areaCode).startsWith(search)));
    } else if (search?.length === SEARCH_FIELD_MAX_LENGTH) {
      setAreaCodeDropDown(searchFilter);
    }
  }, [search]);

  useEffect(() => {
    setAreaCodeDropDown(areaCodeSorted);
  }, [dropdownRef?.current]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        dropdownRef?.current?.close();
      }
    };

    document.addEventListener('mouseup', handleClickOutside as unknown as EventListener);

    return () => {
      document.removeEventListener('mouseup', handleClickOutside as unknown as EventListener);
    };
  }, [dropdownRef]);

  const valueInTextField = [...checked].sort((a, b) => a - b).join(', ') || '';

  return (
    <>
      <FuelTypography size="body-md" weight="bold">
        DDD do motorista
      </FuelTypography>
      <FuelDropdown data-testid="filter-ddd" ref={dropdownRef} fullWidth>
        <FuelTextField
          placeholder="Selecione o DDD"
          inputmode="none"
          readonly
          slot="trigger"
          hasSuffix
          value={valueInTextField}
          style={{ cursor: 'pointer' }}
          data-testid="dropdown-ddd"
        >
          <FuelIcon slot="suffix" name="chevron-down" />
        </FuelTextField>

        <FuelFlex slot="header" defaultProps={{ width: '100%' }}>
          <FuelTextField
            data-testid="search-ddd"
            placeholder="Digite o DDD"
            inputmode="none"
            slot="trigger"
            maxlength={SEARCH_FIELD_MAX_LENGTH}
            hasSuffix
            pattern="[0-9]"
            onKeyUp={handleKeyUp}
            onKeyDown={handleKeyPress}
          >
            <FuelIcon slot="suffix" name="search" />
          </FuelTextField>
        </FuelFlex>
        <FuelFlex
          slot="content"
          data-testid="dropdown-content"
          defaultProps={{
            gap: 'km6',
            flexDirection: 'column',
            customPadding: `0 0 ${dsTheme.padding.km4} ${dsTheme.padding.km4}}`,
          }}
        >
          {search && areaCodeDropDown.length === 0 && !checkedFilterSearch && (
            <FuelCallout
              icon="vehicle-truck-cargo"
              message={`No momento não há motoristas sugeridos com o DDD (${search})`}
              fullWidth
            />
          )}
          {areaCodeDropDown.map(checkBoxMap)}
        </FuelFlex>
        <div slot="button" />
        <FuelButton
          data-testid="select-button"
          variant="primary"
          onClick={() => {
            setSearch('');
            dropdownRef?.current?.close();
          }}
          slot="button"
        >
          Selecionar
        </FuelButton>
      </FuelDropdown>
    </>
  );
};

export default DDD;
